import dayjs from 'dayjs';
/**
 * @interface IFundInfoFormatInfo
 * 基金信息格式化处理
 * 格式化逻辑：
 * 基金为首发基金（即IssueState发行状态为1、2、3、4）：则行内主推基金列表收益率字段显示首发基金；
 * 若基金为非货币基金，收益率字段：显示近1年收益率；
 * 若近1年收益率为空，则取成立以来收益率；若基金为货币基金，收益率字段取七日年化
 */
interface IFundInfoFormatInfo {
  isFirstPublish: boolean;
  isCurrency: boolean;
  newVal: {
    value: unknown;
    name: string;
  };
}

interface IFundInfo {
  [key: string]: any;
}

export const fundInfoFormat = (item: IFundInfo): IFundInfoFormatInfo => {
  // 是否首发基金
  const isFirstPublish = [1, 2, 3, 4].includes(item?.saleStatus);
  // 是否货币基金
  const isCurrency = item?.fundTypeCode === 1109;
  let newVal = {} as any;
  // 若基金为货币基金，收益率字段取七日年化
  if (!isCurrency) {
    // 若基金为非货币基金，收益率字段：显示近1年收益率；若近1年收益率为空，则取成立以来收益率
    newVal =
      item?.lastYear && Number(item.lastYear)
        ? {
            value: item?.lastYear,
            name: '近1年收益率',
          }
        : {
            value: item?.rateOfReturnSinceInception,
            name: '成立以来收益率',
          };
  } else {
    newVal = {
      value: item?.latestWeeklyYield,
      name: '七日年化',
    };
  }
  return { isFirstPublish, isCurrency, newVal };
};

export const numberUpperCase = (digit: string | number) => {
  const numberArr: Array<string> = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
  digit = Number(digit);

  if (digit > 10 || !digit) return '--';
  return numberArr[digit];
};

export const filterClassName = (digit: string | number, defaultName?: string) => {
  const numberArr = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  digit = Number(digit);
  if (digit > 9) return defaultName || '';
  return numberArr[digit];
};

// type: 10周, 20月, 30每两周
export const textFormat = (type: string | number, cycle: string | number, isEmpty = false) => {
  type = Number(type);
  const text = type === 10 ? (isEmpty ? '每周 周' : '每周周') : '每两周 周';

  switch (type) {
    case 10:
      return `${text}${numberUpperCase(cycle)}`;
    case 20:
      return `每月${cycle}号`;
    default:
      return `${text}${numberUpperCase(cycle)}`;
  }
};

// 虚拟键盘.苹果兼容
export const isIPhoneKeyboard = () => {
  let moneyKeyboardWrapProps;
  const isIPhone = new RegExp('\\biPhone\\b|\\biPod\\b', 'i').test(window.navigator.userAgent);
  if (isIPhone) {
    moneyKeyboardWrapProps = {
      onTouchStart: (e) => e.preventDefault(),
    };
  }
  return moneyKeyboardWrapProps;
};

// 虚拟键盘设置了自适应高度,失去焦点时还原高度
export const setKeyboardHeight = () => {
  const keyboard: any = document.querySelector('#am-number-keyboard-container');
  if (keyboard && keyboard.childNodes.length === 0) {
    keyboard.style.height = 0;
  }
};

// 参数startTime/endTime可以包含三种格式: 时间戳格式 16xxx, 格式化字符串 '2021-10-26', Date格式
export const filterTime = (startTime: number | string, endTime?: number | string) => {
  const reg = new RegExp('[0-9]{13}');
  try {
    endTime = endTime || Date.now();
    const s = reg.test(startTime + '') ? new Date(Number(startTime)).getTime() : new Date(startTime).getTime();
    const e = reg.test(endTime + '') ? new Date(Number(endTime)).getTime() : new Date(endTime).getTime();
    if (e <= s) return ''; // 当前时间(endTime)必须大于开始时间,防止电脑时间不准确

    const day = dayjs(e).diff(dayjs(s), 'day');
    const hour = dayjs(e).diff(dayjs(s), 'hour');
    const minute = dayjs(e).diff(dayjs(s), 'minute');
    return day ? `${day}天前` : hour ? `${hour}小时前` : minute ? `${minute}分钟前` : '';
  } catch (err) {
    console.log('filterTime 传入的时间参数格式不正确');
  }
};

// 基金比较进度条计算
export const countLevel = (num: number): { text: string; level: number } => {
  if (num >= 100) return { text: '优秀', level: 5 };
  if (num >= 80 && num < 100) return { text: '良好', level: 4 };
  if (num >= 60 && num < 80) return { text: '中等', level: 3 };
  if (num >= 40 && num < 60) return { text: '一般', level: 2 };
  if (num >= 20 && num < 40) return { text: '较差', level: 1 };
  return { text: '--', level: 0 };
};

// 基金对比,胜负计算
export const compareOutcome = (left: number, right: number) => {
  return left > right
    ? { left: 'victory', right: 'fail' } // 左边胜
    : left < right
    ? { left: 'fail', right: 'victory' } // 右边胜
    : { left: 'victory', right: 'victory' }; // 相等
};

// 针对2.13版本收益率中文和别的字符要区分不同的展示文字大小
export const formatIncomeRate = (list = [], regKey = '', addName = 'showIncomeRate') => {
  const tempList = list.map((item: any, index) => {
    const income = item[regKey] || item.incomeRate + (item.incomeRateIsCustomize === 0 ? '%' : '');
    const ary = (income + '').match(/[\u4e00-\u9fa5]/g);
    if (income === '%') {
      item[addName] = '';
    } else {
      item[addName] = income;
    }
    ary &&
      ary.map((v, k) => {
        item[addName] = item[addName].replace(new RegExp(v, 'g'), `<span class='formatIncomeChinese'>${v}</span>`);
        return v;
      });
    return item;
  });
  return tempList;
};

// 处理产品列表里基金层级问题+income显示问题
export const formatProducts = (list: any) => {
  let formatList = list.map((v) => {
    v = Object.assign({}, v, v.fundProductSimpleInfo ? { ...v.fundProductSimpleInfo } : { ...v });
    return v;
  });
  formatList = formatIncomeRate(formatList);
  return formatList;
};
